import { IconButton, toast } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { canManageSchedules, canUseEvaluation, useAppState } from "@/app";
import { FullscreenWindow, type WindowEntry } from "@/components/modal";
import { notifyScheduleChanged } from "@/page/training/schedules/scheduleBus";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { windowStack } from "@/app/windowStack";
import { openTrainingSessionWindow } from "../trainingSessionWindow";
import { submitViewToListView } from "../trainingSessionFormat";
import { notifyTrainingSubmitted } from "../trainingSubmitBus";
import type { TrainingSubmitWindowParams } from "../trainingSubmitWindow";
import { ScheduleStep } from "./ScheduleStep";
import { ConfigureStep, ConfirmStep, DatasetSelectStep } from "./SubmitWizardSteps";
import { hasScheduleStep } from "./submitWizardFeatures";
import { SUBMIT_STEP_LABELS, type SubmitStep } from "./submitWizardForm";
import { useSubmitWizardController } from "./useSubmitWizardController";
import styles from "./submitWizard.module.css";

// 창 헤더 ←/→ 의 접근성 이름이자 툴팁. 이동할 곳을 표시한다. 범위 밖이면 방향만 표시한다.
function stepNavLabel(steps: readonly SubmitStep[], stepIndex: number, direction: string): string {
  const step = steps[stepIndex];
  return step === undefined ? direction : `${direction}: ${SUBMIT_STEP_LABELS[step]}`;
}

// New Training 은 뒤로/다음/제출 이동을 공유하는 세 스텝 위저드다.
// 제출이 성공하면 이 창을 닫기 전에 세션을 호출부에 알린다.
export function SubmitWizardWindowContent({
  entry,
  active,
}: {
  entry: WindowEntry;
  active: boolean;
}): ReactNode {
  const params = entry.params as TrainingSubmitWindowParams;
  const mode = params.mode ?? "session";
  const { state: appState } = useAppState();
  const navigate = useNavigate();
  const role = appState.currentUser?.role ?? null;
  const permissions = appState.currentUser?.permissions ?? null;
  const canUseIsaacEvaluation = canUseEvaluation(permissions);
  const closeSelf = () => windowStack.close(entry.id);
  const controller = useSubmitWizardController({
    role,
    mode,
    selfNotificationEmail: appState.currentUser?.notificationEmail ?? null,
    canUseIsaacEvaluation,
    onScheduled: () => {
      // 목록 페이지가 열려 있으면 새 스케줄을 받도록 방송한다. 창을 닫으면서 목록으로 가는 경로를 제공한다.
      notifyScheduleChanged();
      // 생성 중에는 닫기를 잠그므로, 먼저 잠금을 풀어야 이 close 가 동작한다(세션 제출과 같은 순서).
      windowStack.update(entry.id, { closable: true });
      closeSelf();
      toast.success("Schedule을 만들었습니다. 다음 실행부터 반복 제출됩니다.", {
        action: { label: "목록 열기", onPress: () => navigate("/training/schedules") },
      });
    },
    onSubmitted: (view) => {
      // 창을 연 위치(페이지 헤더·사이드바)와 관계없이, 열려 있는 세션 목록이 새 세션을 받도록 방송한다.
      notifyTrainingSubmitted();
      // 제출 중에는 닫기를 잠그므로, 먼저 잠금을 풀어야 이 close 가 동작한다.
      windowStack.update(entry.id, { closable: true });
      closeSelf();
      // 상세는 자동으로 열지 않고 열 수 있게만 한다. 제출은 접은 채 실행되는 작업이라
      // 사용자가 하던 일을 창으로 중단시키지 않는다(데이터셋 통합 완료 통지와 같은 규칙).
      toast.success("Training을 제출했습니다. Training 목록에서 진행 상황을 볼 수 있습니다.", {
        action: {
          label: "열기",
          onPress: () => openTrainingSessionWindow(submitViewToListView(view), { owned: true }),
        },
      });
    },
    initialDataset: params.initialDataset,
    prefill: params.prefill,
  });
  const {
    optionsState,
    features,
    step,
    stepIndex,
    canAdvance,
    maxReachableStepIndex,
    canSubmit,
    submitting,
    goBack,
    goNext,
    moveConfigureSection,
    setStep,
    submit,
  } = controller;
  const steps = features.steps;

  // 스케줄 쓰기는 admin 전용이다(BE training.schedule_write_forbidden). 진입 버튼(목록 헤더)은
  // 이미 숨겨져 있지만, 창 복원 등 다른 경로로 열려도 같은 판정으로 막는다(fail closed).
  if (hasScheduleStep(features) && !canManageSchedules(permissions)) {
    return (
      <FullscreenWindow
        open={active}
        title={features.windowTitle}
        onRequestClose={closeSelf}
        onMinimize={() => windowStack.minimize(entry.id)}
        onBackdrop={() => windowStack.minimize(entry.id)}
      >
        <EmptyState size="lg" tone="error">
          Schedule 생성은 admin 권한에서만 가능합니다. 관리자에게 문의해 주세요.
        </EmptyState>
      </FullscreenWindow>
    );
  }

  // 탭도 화살표·푸터 '다음'과 같은 게이트를 쓴다. 탭만 열려 있으면 클릭 한 번으로 미완성
  // 스텝을 건너뛸 수 있어서 나머지를 잠근 의미가 없다. 지금 보고 있는 스텝은 한계선을
  // 넘었어도 남긴다(입력을 지워 뒤 스텝이 잠긴 경우). 사용자가 보던 화면을 바꾸지 않는다.
  const lastEnabledStepIndex = Math.max(maxReachableStepIndex, stepIndex);
  const stepHeader = (
    <div className={styles.stepTabs} role="tablist" aria-label="제출 단계">
      {steps.map((value, index) => (
        <IconButton
          key={value}
          text={`${index + 1} ${SUBMIT_STEP_LABELS[value]}`}
          variant="surface"
          selected={value === step}
          disabled={index > lastEnabledStepIndex}
          onClick={() => setStep(value)}
        />
      ))}
    </div>
  );

  // 스텝 헤더 양옆 ←/→ 버튼과 같은 키 단축키(셸이 소유한다). **앞으로 가는 이동은 푸터
  // '다음'과 같은 게이트(canAdvance)를 쓴다.** 경로마다 게이트가 다르면 화살표로 미완성 스텝을
  // 건너뛸 수 있어서 게이트가 동작하지 않는다. 뒤로는 잠그지 않는다(고친 값을 다시 확인하는 경로).
  //
  // 그래서 창을 연 직후(담은 데이터셋 0)에는 → 가 동작하지 않는다. 이것은 고장이 아니라 게이트가
  // 닫힌 상태다. 헤더의 [→] 버튼이 disabled 로 그 사실을 표시한다.
  // 제출은 상류까지 동기로 왕복해서 오래 걸린다(데이터셋 등록이 건마다 순차다). 그동안 전체화면
  // 창이 화면을 차지할 필요가 없으므로 **먼저 접고** 진행을 toast 로 알린다. 최소화는 언마운트가
  // 아니다(WindowStackHost 는 엔트리를 항상 마운트한다). 그래서 폼·에러 문구·멱등 키가 그대로
  // 유지되고, 실패하면 같은 창을 다시 펼쳐서 고치면 된다.
  //
  // 접힌 동안에는 닫기를 잠근다. 닫으면 훅이 언마운트되어 실패를 알릴 자리가 없어진다.
  // 스케줄 생성은 단건 POST 라 접지 않는다. 창 안에서 결과를 기다리고, 실패 사유는 확정
  // 스텝(submitError)이 그 자리에 표시한다. 멱등 키가 없어서 실패 후 재시도하면 중복 생성될 수
  // 있다. 이 가능성은 확정 스텝 문구가 안내한다.
  const handleScheduleSubmit = async () => {
    // 멱등 키가 없는 POST 라 진행 중에 창이 닫히면 결과를 알릴 자리가 없어진다. 끝날 때까지 잠근다.
    windowStack.update(entry.id, { closable: false });
    let outcome: { ok: boolean };
    try {
      outcome = await submit();
    } finally {
      windowStack.update(entry.id, { closable: true });
    }
    if (!outcome.ok) {
      // 멱등 키가 없는 단건 POST 다. 서버가 반영한 뒤에 실패했을 수 있으므로 재시도 전에 목록
      // 확인을 권한다. 알림 없이 두 개가 생성되는 것이 가장 나쁜 결과다.
      toast.error(
        "Schedule 생성에 실패했습니다. 화면의 사유를 확인하고, 다시 시도하기 전에 목록에 이미 만들어지지 않았는지 확인해 주세요.",
      );
    }
  };

  const handleSubmit = async () => {
    windowStack.minimize(entry.id);
    windowStack.update(entry.id, { closable: false });
    const progressToastId = toast.info("Training을 제출하는 중입니다. 끝나면 알려드립니다.", {
      // 이 코드가 완료 시점을 알므로 시간으로 제거하지 않는다. 제출이 토스트보다 오래 걸린다.
      durationMs: null,
    });
    try {
      const outcome = await submit();
      if (!outcome.ok) {
        toast.error("Training 제출에 실패했습니다. 제출 창에서 사유를 확인해 주세요.", {
          action: { label: "제출 창 열기", onPress: () => windowStack.setActive(entry.id) },
        });
      }
    } finally {
      toast.dismiss(progressToastId);
      // 성공 경로에서는 창이 이미 닫혔다. 그 경우 잠금 해제는 close 가 동작하도록 먼저 실행된다.
      windowStack.update(entry.id, { closable: true });
    }
  };

  const canGoNext = stepIndex < steps.length - 1 && canAdvance;
  const stepNav = {
    onPrev: goBack,
    onNext: goNext,
    prevDisabled: stepIndex === 0,
    nextDisabled: !canGoNext,
    prevLabel: stepNavLabel(steps, stepIndex - 1, "이전"),
    nextLabel: stepNavLabel(steps, stepIndex + 1, "다음"),
  };

  const footer = (
    <div className={styles.footer}>
      <IconButton
        text="뒤로"
        size="sm"
        variant="surface"
        disabled={stepIndex === 0}
        onClick={goBack}
      />
      <div className={styles.footerSpacer} />
      {step === "confirm" ? (
        <IconButton
          text={submitting ? features.submitLabel.busy : features.submitLabel.idle}
          size="sm"
          variant="solid"
          disabled={!canSubmit || submitting}
          onClick={() => void (hasScheduleStep(features) ? handleScheduleSubmit() : handleSubmit())}
        />
      ) : (
        <IconButton text="다음" size="sm" variant="solid" disabled={!canAdvance} onClick={goNext} />
      )}
    </div>
  );

  return (
    <FullscreenWindow
      open={active}
      title={features.windowTitle}
      stepHeader={stepHeader}
      stepNav={stepNav}
      // 세로로 훑을 목록이 있는 스텝은 구성뿐이다. 다른 스텝에서는 전달하지 않으므로 셸이 ↑/↓ 를
      // 포착하지 않고 브라우저 기본 스크롤이 동작한다.
      onArrowUp={step === "configure" ? () => moveConfigureSection(-1) : undefined}
      onArrowDown={step === "configure" ? () => moveConfigureSection(1) : undefined}
      footer={footer}
      // 선택 컨트롤이 스크롤되어 사라지면 안 된다. 셸은 높이만 주고 스크롤 주체는 스텝이
      // 소유한다(①: 왼쪽 선택 열 고정 + 오른쪽 미리보기 스크롤 / ②: 3열 각자 / ③: 스텝 전체가 한 스크롤 영역).
      bodyLayout="fill"
      onMinimize={() => windowStack.minimize(entry.id)}
      onBackdrop={() => windowStack.minimize(entry.id)}
      onRequestClose={closeSelf}
    >
      {/* 옵션(submit-options) 실패만 전체를 막는다. 로딩 중에도 셸·데이터셋을 즉시 표시하고, 각 스텝이
          섹션별로 fan-out 로딩한다. training-infra proxy 왕복이 셸 표시를 늦추지 않게 한다. */}
      {optionsState.status === "error" ? (
        <EmptyState size="lg" tone="error">
          {optionsState.message}
        </EmptyState>
      ) : step === "dataset" ? (
        <DatasetSelectStep controller={controller} />
      ) : step === "configure" ? (
        <ConfigureStep controller={controller} />
      ) : step === "schedule" ? (
        <ScheduleStep controller={controller} />
      ) : (
        <ConfirmStep controller={controller} />
      )}
    </FullscreenWindow>
  );
}
